import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import * as d3 from "d3";

const GraphCanvas = ({
  filteredData,
  layoutType,
  layoutKey,
  typeColors,
  selectedEntityTypes,
  showNavTips,
  showEntityTypesPanel,
  handleNodeClick,
  handleEdgeClick,
  handleNodeHover,
  handleEdgeHover,
  handleZoomIn,
  handleZoomOut,
  handleFitToView,
  handleResetLayoutRef,
  onLayoutUpdate,
  handleLoadConnections,
  /** "dark" | "light" — Explorer embedded uses light surfaces */
  uiTone = "dark",
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const zoomRef = useRef(null);

  // D3 simulation setup
  const setupSimulation = useCallback(() => {
    if (!svgRef.current || !filteredData.nodes.length) return;

    const svg = d3.select(svgRef.current);
    /** Viewport element — zoom attaches here so extent matches visible area (not oversized SVG). */
    const container = svg.node().parentNode;
    const zoomHost = d3.select(container);
    const width = container.clientWidth || 800;
    const height = container.clientHeight || 600;

    /** Drawing surface matches the visible container exactly — the graph
     *  always fits the available space (pan/zoom via transform, no scrollbars). */
    const canvasWidth = width;
    const canvasHeight = height;

    const isLight = uiTone === "light";
    const markerFill = isLight ? "#64748b" : "#666";
    const svgBg = isLight ? "#f8fafc" : "#1f2937";
    const edgeStrokeDefault = isLight ? "#94a3b8" : "#666";
    const edgeLabelFill = isLight ? "#475569" : "#999";
    const nodeLabelFill = isLight ? "#0f172a" : "#ffffff";

    // Clear previous content
    svg.selectAll("*").remove();

    svg
      .attr("width", canvasWidth)
      .attr("height", canvasHeight)
      // Default SVG overflow is hidden — zoom transforms draw outside the nominal box; don't clip.
      .attr("overflow", "visible")
      .style("background", svgBg)
      .style("display", "block");

    // Create container group
    const containerGroup = svg.append("g");

    // Define arrowhead marker for directed edges (single-direction)
    const defs = svg.append("defs");
    defs
      .append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 0 10 10")
      .attr("refX", 10)
      .attr("refY", 5)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto-start-reverse")
      .append("path")
      .attr("d", "M 0 0 L 10 5 L 0 10 z")
      .attr("fill", markerFill);

    // Zoom on the viewport container (not the huge SVG) so button/wheel zoom centers on-screen.
    const zoom = d3
      .zoom()
      .scaleExtent([0.05, 14])
      .translateExtent([
        [-1000000, -1000000],
        [1000000, 1000000],
      ])
      .on("zoom", (event) => {
        containerGroup.attr("transform", event.transform);
      });

    zoomHost.call(zoom);

    // Filter and consolidate edges - merge multiple relationships between same nodes
    const edgeMap = new Map();

    const relIsProposed = (edge) =>
      (edge.data && edge.data.relationshipStatus === "Proposed") ||
      edge.relationshipStatus === "Proposed";

    filteredData.edges.forEach((edge) => {
      // Ensure edge has valid source and target nodes
      if (
        !edge.source ||
        !edge.target ||
        !filteredData.nodes.find((n) => n.id === edge.source.id) ||
        !filteredData.nodes.find((n) => n.id === edge.target.id)
      ) {
        return;
      }

      const sourceId = edge.source.id || edge.source;
      const targetId = edge.target.id || edge.target;
      const edgeKey = `${sourceId}->${targetId}`;

      const relationshipType =
        edge.type || edge.data?.relationshipType || "Unknown";

      if (edgeMap.has(edgeKey)) {
        // Consolidate: add this relationship type to existing edge
        const existingEdge = edgeMap.get(edgeKey);
        existingEdge._hasProposedRel =
          Boolean(existingEdge._hasProposedRel) || relIsProposed(edge);
        const existingTypes = existingEdge.relationshipTypes || [
          existingEdge.type || existingEdge.data?.relationshipType || "Unknown",
        ];

        // Add new type if not already present
        if (!existingTypes.includes(relationshipType)) {
          existingTypes.push(relationshipType);
        }

        // Update the consolidated edge
        existingEdge.relationshipTypes = existingTypes;
        existingEdge.type = existingTypes.join(", ");
        existingEdge.data = {
          ...existingEdge.data,
          relationshipType: existingTypes.join(", "),
          relationshipTypes: existingTypes,
        };

        // Keep track of all relationship IDs
        if (!existingEdge.relationshipIds) {
          existingEdge.relationshipIds = [existingEdge.id];
        }
        if (!existingEdge.relationshipIds.includes(edge.id)) {
          existingEdge.relationshipIds.push(edge.id);
        }
      } else {
        // First edge between these nodes
        edgeMap.set(edgeKey, {
          ...edge,
          id: edgeKey, // Use consolidated key as ID for D3
          relationshipTypes: [relationshipType],
          relationshipIds: [edge.id],
          type: relationshipType,
          _hasProposedRel: relIsProposed(edge),
          data: {
            ...edge.data,
            relationshipType: relationshipType,
            relationshipTypes: [relationshipType],
          },
        });
      }
    });

    const processedEdges = Array.from(edgeMap.values());

    // Set up force simulation
    const simulation = d3
      .forceSimulation(filteredData.nodes)
      .force(
        "link",
        d3
          .forceLink(processedEdges)
          .id((d) => d.id)
          .distance(100)
      )
      .force("charge", d3.forceManyBody().strength(-300))
      .force("center", d3.forceCenter(canvasWidth / 2, canvasHeight / 2))
      .force("collision", d3.forceCollide().radius(30));

    // Create links container
    const linksGroup = containerGroup.append("g").attr("class", "links");

    // Create straight lines for circular layout
    const defaultEdgeStroke = (d) =>
      d._hasProposedRel ? "#f59e0b" : edgeStrokeDefault;
    const defaultEdgeDash = (d) => (d._hasProposedRel ? "6 4" : null);

    const links = linksGroup
      .selectAll("line")
      .data(processedEdges)
      .enter()
      .append("line")
      .attr("stroke", defaultEdgeStroke)
      .attr("stroke-width", 2)
      .attr("stroke-opacity", 0.6)
      .attr("stroke-dasharray", defaultEdgeDash)
      .attr("marker-end", "url(#arrowhead)")
      .style("cursor", "pointer")
      .on("click", handleEdgeClick)
      .on("mouseover", function (event, d) {
        d3.select(this)
          .attr("stroke", "#3b82f6")
          .attr("stroke-width", 3)
          .attr("stroke-opacity", 1);
        handleEdgeHover(event, d);
      })
      .on("mouseout", function (event, d) {
        d3.select(this)
          .attr("stroke", defaultEdgeStroke(d))
          .attr("stroke-width", 2)
          .attr("stroke-opacity", 0.6)
          .attr("stroke-dasharray", defaultEdgeDash(d) || "none");
        if (typeof d3 !== "undefined") {
          d3.selectAll(".edge-label").style("opacity", 0.7);
        }
      });

    // Create drag behavior
    const drag = d3
      .drag()
      .on("start", (event, d) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        // Keep the node permanently fixed in its dragged position
        // Don't release the fixed position - let it stay where user placed it
      });

    // Use all filtered nodes for circular layout
    const visibleNodes = filteredData.nodes;

    // Create node groups (circle + label together)
    const nodeGroups = containerGroup
      .append("g")
      .selectAll("g")
      .data(visibleNodes)
      .enter()
      .append("g")
      .attr("class", "node-group")
      .style("cursor", "pointer")
      .call(drag)
      .on("click", (event, d) => {
        if (handleNodeClick) {
          handleNodeClick(event, d);
        }
      })
      .on("mouseover", handleNodeHover)
      .on("mouseout", () => {
        if (typeof d3 !== "undefined") {
          d3.selectAll(".node-label").style("opacity", 1);
        }
      });

    // Add circles to node groups
    const circles = nodeGroups
      .append("circle")
      .attr("r", 15)
      .attr("fill", (d) => {
        const colors = typeColors[d.type] || typeColors["default"];
        return colors.bg;
      })
      .attr("stroke", (d) => {
        if (d.data?.approvalStatus === "Proposed") {
          return "#f59e0b";
        }
        const colors = typeColors[d.type] || typeColors["default"];
        return colors.border;
      })
      .attr("stroke-width", (d) =>
        d.data?.approvalStatus === "Proposed" ? 3 : 2
      )
      .attr("stroke-dasharray", (d) =>
        d.data?.approvalStatus === "Proposed" ? "4 3" : null
      );

    // Add labels to node groups
    nodeGroups
      .append("text")
      .attr("class", "node-label")
      .attr("text-anchor", "middle")
      .attr("x", 0)
      .attr("dy", 5)
      .attr("fill", nodeLabelFill)
      .attr("font-size", "10px")
      .attr("font-weight", "bold")
      .attr("stroke", "none")
      .attr("stroke-width", "0")
      .text((d) => d.name || d.data?.label || "Unknown")
      .style("opacity", 1)
      .style("pointer-events", "none"); // Prevent text from blocking clicks

    const edgeLabels = containerGroup
      .append("g")
      .selectAll("text")
      .data(processedEdges)
      .enter()
      .append("text")
      .attr("class", "edge-label")
      .attr("text-anchor", "middle")
      .attr("fill", edgeLabelFill)
      .attr("font-size", "10px")
      .text((d) => {
        // Use consolidated relationship types if available
        if (d.relationshipTypes && d.relationshipTypes.length > 1) {
          return d.relationshipTypes.join(", ");
        }
        return d.type || d.data?.relationshipType || "Unknown";
      })
      .style("opacity", 0.7);

    // Update positions on tick
    simulation.on("tick", () => {
      // Use straight lines for circular layout
      links
        .attr("x1", (d) => {
          const sourceNode = filteredData.nodes.find(
            (n) => n.id === (d.source?.id || d.source)
          );
          return sourceNode?.x || d.source.x || 0;
        })
        .attr("y1", (d) => {
          const sourceNode = filteredData.nodes.find(
            (n) => n.id === (d.source?.id || d.source)
          );
          return sourceNode?.y || d.source.y || 0;
        })
        .attr("x2", (d) => {
          const targetNode = filteredData.nodes.find(
            (n) => n.id === (d.target?.id || d.target)
          );
          return targetNode?.x || d.target.x || 0;
        })
        .attr("y2", (d) => {
          const targetNode = filteredData.nodes.find(
            (n) => n.id === (d.target?.id || d.target)
          );
          return targetNode?.y || d.target.y || 0;
        });

      // Move nodes
      nodeGroups.attr("transform", (d) => `translate(${d.x},${d.y})`);

      // Update edge labels
      edgeLabels
        .attr("x", (d) => {
          const sourceNode = filteredData.nodes.find(
            (n) => n.id === (d.source?.id || d.source)
          );
          const targetNode = filteredData.nodes.find(
            (n) => n.id === (d.target?.id || d.target)
          );
          return (
            ((sourceNode?.x || d.source.x || 0) +
              (targetNode?.x || d.target.x || 0)) /
            2
          );
        })
        .attr("y", (d) => {
          const sourceNode = filteredData.nodes.find(
            (n) => n.id === (d.source?.id || d.source)
          );
          const targetNode = filteredData.nodes.find(
            (n) => n.id === (d.target?.id || d.target)
          );
          return (
            ((sourceNode?.y || d.source.y || 0) +
              (targetNode?.y || d.target.y || 0)) /
            2
          );
        });
    });

    // Store references
    simulationRef.current = simulation;
    zoomRef.current = zoom;

    // Initial zoom to fit
    setTimeout(() => {
      const bounds = containerGroup.node().getBBox();

      // Check for valid bounds
      if (
        bounds.width > 0 &&
        bounds.height > 0 &&
        !isNaN(bounds.x) &&
        !isNaN(bounds.y) &&
        !isNaN(bounds.width) &&
        !isNaN(bounds.height)
      ) {
        const fullWidth = container.clientWidth;
        const fullHeight = container.clientHeight;
        const widthScale = fullWidth / bounds.width;
        const heightScale = fullHeight / bounds.height;
        const scale = Math.min(widthScale, heightScale) * 0.92;
        const translate = [
          fullWidth / 2 - scale * (bounds.x + bounds.width / 2),
          fullHeight / 2 - scale * (bounds.y + bounds.height / 2),
        ];

        // Check for valid translate values
        if (!isNaN(translate[0]) && !isNaN(translate[1]) && !isNaN(scale)) {
          zoomHost
            .transition()
            .duration(750)
            .call(
              zoom.transform,
              d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
            );
        }
      }
    }, 100);
  }, [
    filteredData,
    typeColors,
    handleNodeClick,
    handleEdgeClick,
    handleNodeHover,
    handleEdgeHover,
    layoutType,
    uiTone,
  ]);

  // Effect to setup simulation when data changes
  useEffect(() => {
    setupSimulation();
  }, [setupSimulation, layoutKey]);

  // Effect to handle layout changes
  useEffect(() => {
    if (!simulationRef.current || !svgRef.current) return;

    const simulation = simulationRef.current;
    const svg = d3.select(svgRef.current);
    const svgNode = svgRef.current;
    const container = svg.node()?.parentNode;
    const cw = svgNode?.clientWidth || container?.clientWidth || 800;
    const ch = svgNode?.clientHeight || container?.clientHeight || 600;

    if (layoutType === "circular") {
      // Position nodes in a flower pattern with origin in center (matches oversized canvas)
      const centerX = cw / 2;
      const centerY = ch / 2;
      const radius = 200;

      // Find the origin/central node (first node or node with most connections)
      const originNode =
        filteredData.nodes.length > 0
          ? filteredData.nodes.reduce((prev, current) => {
              const prevConnections = filteredData.edges.filter(
                (e) => e.source.id === prev.id || e.target.id === prev.id
              ).length;
              const currentConnections = filteredData.edges.filter(
                (e) => e.source.id === current.id || e.target.id === current.id
              ).length;
              return currentConnections > prevConnections ? current : prev;
            })
          : null;

      // Position the origin node in the center (if it exists)
      if (originNode) {
        originNode.x = centerX;
        originNode.y = centerY;
        originNode.fx = centerX;
        originNode.fy = centerY;
      }

      // Position other nodes around the center like flower petals
      const otherNodes = originNode
        ? filteredData.nodes.filter((node) => node.id !== originNode.id)
        : filteredData.nodes;
      const angleStep = (2 * Math.PI) / Math.max(otherNodes.length, 1);

      otherNodes.forEach((node, i) => {
        node.x = centerX + radius * Math.cos(i * angleStep);
        node.y = centerY + radius * Math.sin(i * angleStep);
        // Fix the position to prevent simulation from moving it
        node.fx = node.x;
        node.fy = node.y;
      });

      simulation.force("position", null);
      simulation.force("y", null);

      // Restart simulation to update positions
      simulation.restart();
    }
  }, [layoutType, filteredData.nodes.length, layoutKey]);

  // Expose zoom functions
  useEffect(() => {
    if (handleResetLayoutRef) {
      handleResetLayoutRef.current = () => {
        if (simulationRef.current) {
          simulationRef.current.alpha(0.3).restart();
        }
      };
    }

    if (handleZoomIn) {
      handleZoomIn.current = () => {
        if (zoomRef.current && svgRef.current && typeof d3 !== "undefined") {
          const zoomHost = d3.select(svgRef.current.parentNode);
          zoomHost.transition().duration(300).call(zoomRef.current.scaleBy, 1.5);
        }
      };
    }

    if (handleZoomOut) {
      handleZoomOut.current = () => {
        if (zoomRef.current && svgRef.current && typeof d3 !== "undefined") {
          const zoomHost = d3.select(svgRef.current.parentNode);
          zoomHost
            .transition()
            .duration(300)
            .call(zoomRef.current.scaleBy, 1 / 1.5);
        }
      };
    }

    if (handleFitToView) {
      handleFitToView.current = () => {
        if (zoomRef.current && svgRef.current && typeof d3 !== "undefined") {
          const svg = d3.select(svgRef.current);
          const container = svg.node().parentNode;
          const zoomHost = d3.select(container);
          const containerGroup = svg.select("g");

          if (containerGroup.node()) {
            const bounds = containerGroup.node().getBBox();

            // Check for valid bounds
            if (
              bounds.width > 0 &&
              bounds.height > 0 &&
              !isNaN(bounds.x) &&
              !isNaN(bounds.y) &&
              !isNaN(bounds.width) &&
              !isNaN(bounds.height)
            ) {
              const fullWidth = container.clientWidth;
              const fullHeight = container.clientHeight;
              const widthScale = fullWidth / bounds.width;
              const heightScale = fullHeight / bounds.height;
              const scale = Math.min(widthScale, heightScale) * 0.92;
              const translate = [
                fullWidth / 2 - scale * (bounds.x + bounds.width / 2),
                fullHeight / 2 - scale * (bounds.y + bounds.height / 2),
              ];

              // Check for valid translate values
              if (
                !isNaN(translate[0]) &&
                !isNaN(translate[1]) &&
                !isNaN(scale)
              ) {
                zoomHost
                  .transition()
                  .duration(750)
                  .call(
                    zoomRef.current.transform,
                    d3.zoomIdentity
                      .translate(translate[0], translate[1])
                      .scale(scale)
                  );
              }
            }
          }
        }
      };
    }
  }, [handleResetLayoutRef, handleZoomIn, handleZoomOut, handleFitToView]);

  // Keyboard navigation for infinite scrolling and zoom shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!svgRef.current || !zoomRef.current) return;

      const svg = d3.select(svgRef.current);
      const zoomHostNode = svg.node().parentNode;
      const zoomHost = d3.select(zoomHostNode);
      const zoom = zoomRef.current;
      const currentTransform = d3.zoomTransform(zoomHostNode);

      let newTransform = currentTransform;
      const panDistance = 50; // Distance to pan per key press

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          newTransform = currentTransform.translate(0, panDistance);
          break;
        case "ArrowDown":
          event.preventDefault();
          newTransform = currentTransform.translate(0, -panDistance);
          break;
        case "ArrowLeft":
          event.preventDefault();
          newTransform = currentTransform.translate(panDistance, 0);
          break;
        case "ArrowRight":
          event.preventDefault();
          newTransform = currentTransform.translate(-panDistance, 0);
          break;
        case "+":
        case "=":
          event.preventDefault();
          zoomHost.transition().duration(300).call(zoom.scaleBy, 1.5);
          return;
        case "-":
          event.preventDefault();
          zoomHost.transition().duration(300).call(zoom.scaleBy, 1 / 1.5);
          return;
        case "0":
          event.preventDefault();
          // Fit to view functionality
          if (svgRef.current) {
            const container = svg.node().parentNode;
            const containerGroup = svg.select("g");

            if (containerGroup.node()) {
              const bounds = containerGroup.node().getBBox();

              if (
                bounds.width > 0 &&
                bounds.height > 0 &&
                !isNaN(bounds.x) &&
                !isNaN(bounds.y) &&
                !isNaN(bounds.width) &&
                !isNaN(bounds.height)
              ) {
                const fullWidth = container.clientWidth;
                const fullHeight = container.clientHeight;
                const widthScale = fullWidth / bounds.width;
                const heightScale = fullHeight / bounds.height;
                const scale = Math.min(widthScale, heightScale) * 0.92;
                const translate = [
                  fullWidth / 2 - scale * (bounds.x + bounds.width / 2),
                  fullHeight / 2 - scale * (bounds.y + bounds.height / 2),
                ];

                if (
                  !isNaN(translate[0]) &&
                  !isNaN(translate[1]) &&
                  !isNaN(scale)
                ) {
                  zoomHost
                    .transition()
                    .duration(750)
                    .call(
                      zoom.transform,
                      d3.zoomIdentity
                        .translate(translate[0], translate[1])
                        .scale(scale)
                    );
                }
              }
            }
          }
          return;
        default:
          return; // Don't prevent default for other keys
      }

      zoomHost.transition().duration(200).call(zoom.transform, newTransform);
    };

    // Add event listener to the document
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Keep the SVG matched to its container and refit when the pane resizes.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let first = true;
    let timer = null;
    const observer = new ResizeObserver(() => {
      if (first) {
        first = false;
        return;
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (svgRef.current) {
          d3.select(svgRef.current)
            .attr("width", el.clientWidth)
            .attr("height", el.clientHeight);
        }
        handleFitToView.current?.();
      }, 150);
    });
    observer.observe(el);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [handleFitToView]);

  return (
    <div className="relative min-h-0 min-w-0 flex-1">
      <div ref={containerRef} className="graph-container absolute inset-0 overflow-hidden">
        <svg ref={svgRef} className="max-w-none overflow-visible" />
      </div>
    </div>
  );
};

export default GraphCanvas;
